import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import OrganizationForm from './OrganisationForm.tsx';
import ProfileForm from './ProfileForm.tsx';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { createUserFormDataAtom, createUserFormDataErrorsAtom, organisationFormDataAtom, organisationFormDataErrorAtom } from '../../../../Services/_states/index.js';
import { ManagementActions } from '../../../../Services/_actions/index.js';
import { useSnackBar } from '../../../FlowBuilder/Common/SnackBar/index.tsx';
import CreateUserForm from '../../User/CreateUserForm.tsx';
import { validateOrganisationForm, validateProfileForm, validateUserForm } from '../../validations.ts';
import { useNavigate } from 'react-router';

const steps = ['Organisation', 'Profile', 'User'];

export default function OrganisationOnboarding() {
    const [organisationFormData, setOrganisationFormData] = useRecoilState(organisationFormDataAtom);
    const resetOranisationFormData = useResetRecoilState(organisationFormDataAtom);
    const setOrganisationFormDataErrors = useSetRecoilState(organisationFormDataErrorAtom);
    const [profileFormData, setProfileFormData] = React.useState({ clone_from_profile_id: "", new_organisation_id: "", new_name: "", selectedProfile: null });
    const [profileFormDataErrors, setProfileFormDataErrors] = React.useState({});
    const [userFormData, setUserFormData] = useRecoilState(createUserFormDataAtom);
    const resetUserFormData = useResetRecoilState(createUserFormDataAtom);
    const setUserFormDataErrors = useSetRecoilState(createUserFormDataErrorsAtom);
    const [activeStep, setActiveStep] = React.useState(0);
    const [skipped, setSkipped] = React.useState(new Set<number>());
    const actions = ManagementActions();
    const { showSnackBar } = useSnackBar();
    const navigate = useNavigate();
    const isStepOptional = (_step: number) => {
        return false;
    };

    const isStepSkipped = (step: number) => {
        return skipped.has(step);
    };

    const handleNext = async () => {
        let newSkipped = skipped;
        if (activeStep === 0) {
            let result = validateOrganisationForm(organisationFormData);
            setOrganisationFormDataErrors(result);
            if (Object.keys(result).length === 0) {
                let res = await actions.createOrganisation(organisationFormData);
                if (res?.status === 200) {
                    setProfileFormData({ ...profileFormData, new_organisation_id: res?.data?.data?.organisation_id })
                    setOrganisationFormData({ ...organisationFormData, ...res?.data?.data });
                    setUserFormData({ ...userFormData, organisation: res?.data?.data })
                    showSnackBar("Organisation Created Successfully", "success");
                } else {
                    showSnackBar("Error in creating organisation", "error");
                }
            } else {
                showSnackBar("Error in creating organisation", "error");
                return 0;
            }
        } else if (activeStep === 1) {
            let result = validateProfileForm(profileFormData);
            setProfileFormDataErrors(result);
            if (Object.keys(result).length === 0) {
                let res = await actions.cloneProfileApi(profileFormData);
                setUserFormData({ ...userFormData, profile: res?.data?.data })
                if (res?.status === 200) {
                    showSnackBar("Profile Created Successfully", "success");
                } else {
                    showSnackBar("Profile is not Created Successfully", "error");
                    return;
                }
            } else {
                showSnackBar("Profile is not Created Successfully", "error");
                return;
            }
        } else if (activeStep === 2) {
            const dataToPost = {
                organisation: userFormData?.organisation?.organisation_id,
                username: userFormData?.username || '',
                // password: userFormData?.password,
                // confirm_password: userFormData?.confirm_password,
                first_name: userFormData?.first_name,
                last_name: userFormData?.last_name,
                email: userFormData?.email,
                // role: userFormData?.role?.id,
                mobile: userFormData?.mobile,
                is_superuser: userFormData?.is_superuser,
                // location:
                //   [{ state: "Maharashtra", "city": ["Mumbai", "Pune"] },
                //   { state: "Delhi", "city": [] }],
                user_type: userFormData?.profile?.name || '',
                profile: userFormData?.profile?.id || '',
            }
            let result = validateUserForm(dataToPost);
            setUserFormDataErrors(result);
            if (Object.keys(result).length === 0) {
                let res: any = await actions.createUser(dataToPost);
                if (res?.status === 200) {
                    showSnackBar('User created successfully!', 'success');
                } else if (res?.status === 400) {
                    if (res?.data?.data?.general_error) {
                        const errors: any = {}
                        if (res?.data?.data?.general_error?.username) {
                            errors.username = res?.data?.data?.general_error?.username?.join(",")
                        }
                        if (res?.data?.data?.general_error?.mobile) {
                            errors.mobile = res?.data?.data?.general_error?.mobile?.join(",")
                        }
                        setUserFormDataErrors(errors)
                    } else {
                        showSnackBar('User is not created successfully!', 'error');
                    }
                    return;
                }
            } else {
                showSnackBar("User is not Created Successfully", "error");
                return;
            }

        }

        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values());
            newSkipped.delete(activeStep);
        }
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setSkipped(newSkipped);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleSkip = () => {
        if (!isStepOptional(activeStep)) {
            // You probably want to guard against something like this,
            // it should never occur unless someone's actively trying to break something.
            throw new Error("You can't skip a step that isn't optional.");
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setSkipped((prevSkipped) => {
            const newSkipped = new Set(prevSkipped.values());
            newSkipped.add(activeStep);
            return newSkipped;
        });
    };

    const handleReset = () => {
        setUserFormDataErrors({});
        setProfileFormDataErrors({});
        setOrganisationFormDataErrors({});
        resetUserFormData();
        resetOranisationFormData();
        setActiveStep(0);
        navigate("/r/management/organisation-list");
    };

    const renderStepContent = (step: number) => {
        switch (step) {
            case 0:
                return <OrganizationForm />;
            case 1:
                return <ProfileForm formData={profileFormData} setFormData={setProfileFormData} errors={profileFormDataErrors} />;
            case 2:
                return <CreateUserForm />;
            default:
                return <Typography>Unknown Step</Typography>;
        }
    };
    return (
        <Box
            sx={{
                padding: "20px",
                maxWidth: "900px",
                margin: "auto",
                backgroundColor: "#f9f9f9",
                borderRadius: "8px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            }}
        >
            <Stepper activeStep={activeStep}>
                {steps.map((label, index) => {
                    const stepProps: { completed?: boolean } = {};
                    const labelProps: {
                        optional?: React.ReactNode;
                    } = {};
                    if (isStepOptional(index)) {
                        labelProps.optional = (
                            <Typography variant="caption">Optional</Typography>
                        );
                    }
                    if (isStepSkipped(index)) {
                        stepProps.completed = false;
                    }
                    return (
                        <Step key={label} {...stepProps}>
                            <StepLabel {...labelProps}>{label}</StepLabel>
                        </Step>
                    );
                })}
            </Stepper>
            {activeStep === steps.length ? (
                <React.Fragment>
                    <Typography sx={{ mt: 2, mb: 1 }}>
                        All steps completed - you&apos;re finished
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                        <Box sx={{ flex: '1 1 auto' }} />
                        <Button onClick={handleReset}>Back to Organisations</Button>
                    </Box>
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <Box sx={{ mt: 2, mb: 1 }}>{renderStepContent(activeStep)}</Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                        <Box sx={{ flex: '1 1 auto' }} />
                        {/* {isStepOptional(activeStep) && (
                            <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                                Skip
                            </Button>
                        )} */}
                        <Button onClick={handleNext}>
                            {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                        </Button>
                    </Box>
                </React.Fragment>
            )}
        </Box>
    );
}
